import React, { useEffect, useMemo, useState } from 'react';
import {
  PERF_HUB_SHORTLIST_INTENTS,
  initialShortlistIntentsForModal,
  isSuggestedShortlistIntentForMany,
  isSuggestedShortlistIntent,
} from './perfHubShortlistTags';

/**
 * Shortlist modal — single or multiple creators; multi-select intent tags.
 */
export default function PerfHubShortlistModal({
  open,
  onClose,
  creators = [],
  campaignName,
  crmList,
  busy,
  onSave,
}) {
  const [note, setNote] = useState('');
  const [selected, setSelected] = useState(() => new Set());

  const list = useMemo(() => (creators || []).filter((c) => c?.row_id || c?.instagram_handle), [creators]);
  const isBulk = list.length > 1;
  const primary = list[0];

  const listKey = list.map((c) => c.row_id || c.instagram_handle).join(',');

  useEffect(() => {
    if (!open || !list.length) return;
    setSelected(initialShortlistIntentsForModal(list, crmList));
    setNote('');
  }, [open, listKey, crmList]);

  if (!open || !list.length) return null;

  const toggleIntent = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        if (next.size <= 1) return next;
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSave = () => {
    const intents = PERF_HUB_SHORTLIST_INTENTS.map((o) => o.id).filter((id) => selected.has(id));
    if (!intents.length) return;
    onSave({ creators: list, intents, note: note.trim() });
  };

  const handlesPreview = list
    .slice(0, 4)
    .map((c) => `@${(c.instagram_handle || '').replace(/^@/, '')}`)
    .join(', ');
  const handlesMore = list.length > 4 ? ` +${list.length - 4} more` : '';

  return (
    <div className="ph-econ-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className={`ph-econ-modal ph-shortlist-modal ${isBulk ? 'ph-shortlist-modal--bulk' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ph-shortlist-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ph-econ-modal-head ph-shortlist-modal-head">
          <h3 id="ph-shortlist-title">
            {isBulk ? `Add ${list.length} creators to shortlist` : 'Add to shortlist'}
          </h3>
          <p className="ph-econ-modal-sub">
            {isBulk ? (
              <>
                {handlesPreview}
                {handlesMore}
                {campaignName ? ` · ${campaignName}` : ''}
              </>
            ) : (
              <>
                <strong>@{primary.instagram_handle}</strong>
                {campaignName ? ` · ${campaignName}` : ''}
              </>
            )}
          </p>
          {isBulk && (
            <p className="ph-shortlist-bulk-hint">
              The same tags and note will apply to every selected creator.
            </p>
          )}
          <button
            type="button"
            className="ph-econ-modal-close"
            aria-label="Close"
            disabled={busy}
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="ph-shortlist-modal-body">
          <fieldset className="ph-shortlist-intents">
            <legend className="ph-shortlist-legend">
              What&apos;s next? <span className="ph-muted">(select all that apply)</span>
            </legend>
            {PERF_HUB_SHORTLIST_INTENTS.map((opt) => {
              const checked = selected.has(opt.id);
              const suggested = isBulk
                ? isSuggestedShortlistIntentForMany(opt.id, list)
                : isSuggestedShortlistIntent(opt.id, primary);
              return (
                <label
                  key={opt.id}
                  className={`ph-shortlist-intent ${checked ? 'active' : ''}`}
                >
                  <input
                    type="checkbox"
                    className="ph-shortlist-intent-check"
                    checked={checked}
                    onChange={() => toggleIntent(opt.id)}
                  />
                  <span className="ph-shortlist-intent-body">
                    <span className="ph-shortlist-intent-label-row">
                      <span className="ph-shortlist-intent-label">{opt.label}</span>
                      {suggested && (
                        <span className="ph-shortlist-suggested-pill">Suggested</span>
                      )}
                    </span>
                    <span className="ph-shortlist-intent-desc">{opt.description}</span>
                  </span>
                </label>
              );
            })}
          </fieldset>

          {!isBulk && primary.ugc_score != null && (
            <p className="ph-shortlist-metric-hint">
              UGC fit <strong>{primary.ugc_score}</strong>/100
              {primary.metrics?.media_type ? ` · ${primary.metrics.media_type}` : ''}
              {primary.metrics?.saved != null ? ` · ${primary.metrics.saved} saves` : ''}
            </p>
          )}

          <label className="ph-shortlist-note-label">
            Note <span className="ph-muted">(optional)</span>
            <textarea
              className="ph-shortlist-note"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                isBulk
                  ? 'Applies to all selected creators'
                  : 'e.g. strong hook, product demo reel'
              }
            />
          </label>

          {!isBulk && (primary.permalink || primary.post_url) && (
            <p className="ph-shortlist-link">
              <a
                href={primary.permalink || primary.post_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open post on Instagram ↗
              </a>
            </p>
          )}

          <div className="ph-econ-modal-footer ph-shortlist-footer">
            <button type="button" className="btn-secondary" disabled={busy} onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={busy || selected.size === 0}
              onClick={handleSave}
            >
              {isBulk ? `Save ${list.length} to shortlist` : 'Save to shortlist'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
